import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { RegisterForm } from '@/features/auth/components/RegisterForm';
import type { RegisterInput } from '@socialsoup/shared';

export function RegisterPage() {
  const { register: registerMutation, isAuthenticated, user } = useAuth();

  // If already authenticated, redirect to correct portal
  if (isAuthenticated && user) {
    const portalPaths: Record<string, string> = {
      ADMIN: '/admin',
      INFLUENCER: '/influencer',
      CLIENT: '/client',
      AGENT: '/agent',
    };
    return <Navigate to={portalPaths[user.role] ?? '/'} replace />;
  }

  const handleRegister = (data: RegisterInput) => {
    registerMutation.mutate(data);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-light p-4">
      <div className="w-full max-w-sm">
        {/* Branding */}
        <div className="text-center mb-8">
          <svg
            viewBox="0 0 336.8 39.2"
            xmlns="http://www.w3.org/2000/svg"
            className="h-8 w-auto mx-auto mb-4 fill-content-primary"
            aria-label="Social Soup"
            role="img"
          >
            <path d="M32.3,28.1C32.3,35,26.9,39,16.9,39C4.3,39.2,0,32.1,0,25.5h7.2c0,5.6,3.9,10.2,10,10.2c4.5,0,7.8-1.8,7.8-5.8 c0-4.2-2.7-6.4-9.1-7.5S1.3,19.8,1.3,11.6C1.3,5.3,6.6,1,16,1c9.7,0,14.7,4.9,14.7,12.6h-7.1c0-5.3-1.8-9.2-8.2-9.2 c-4.4,0-6.7,2.7-6.7,6c0,4.1,4.4,5.7,10.6,6.7C24.8,18,32.3,20.1,32.3,28.1" />
            <path d="M103.4,24.8h7.2c0,7.7-5.8,14.4-17.2,14.4c-10.8,0-18.1-7.6-18.1-19.1C75.3,9,82,1,93.3,1c11.1,0,17.2,6,17.2,14.3h-7.2 c0-5.4-3-10.9-10-10.9C84.9,4.3,82.8,13,82.8,20c0,6.8,2.6,15.6,10.6,15.6C100.7,35.7,103.4,30.2,103.4,24.8" />
            <rect x="114.3" y="1.7" width="7.2" height="36.7" />
            <path d="M145.5,1.7l15.3,36.7h-7.3L149.6,28h-14l-4.2,10.4h-7.3l15.6-36.7C139.8,1.7,145.5,1.7,145.5,1.7z M137.2,23.8h10.9 l-5.4-16.5h-0.1L137.2,23.8z" />
            <path d="M170.8,34.3h18.4v4.2h-25.5V1.7h7.2V34.3z" />
            <path d="M53.6,1C41.5,1,34.5,9,34.5,20.1c0,11.4,7.7,19.1,19.1,19.1s19-7.6,19-19.1C72.6,9,65.6,1,53.6,1 M65,22.4 c-1.8,6.6-6.5,15.1-15.5,12.8S40.1,24.1,42,17.6c1.9-6.8,6.2-15.1,15.7-12.7S66.8,15.5,65,22.4" />
            <path d="M295.9,1.7h7.2v22.1c0,10.5-5.1,15.3-15.4,15.3s-15.4-4.6-15.4-15.3V1.7h7.2v22.2c0,8.8,3,11.6,8.2,11.6s8.2-3.1,8.2-11.6 L295.9,1.7L295.9,1.7z" />
            <path d="M336.8,12.5c0,9.4-8.3,10.7-14.9,10.7h-7.7v15.3H307V1.7h14.9C328.5,1.7,336.8,3.1,336.8,12.5 M329.4,12.5 c0-3.3-2.2-6.6-7.2-6.6h-7.9V19h7.9C327.8,19,329.4,15.8,329.4,12.5" />
            <path d="M229.1,28.1c0,6.9-5.4,10.9-15.4,10.9c-12.6,0-16.9-7-16.9-13.7h7.2c0,5.6,3.9,10.2,10,10.2c4.5,0,7.8-1.8,7.8-5.8 c0-4.2-2.7-6.4-9.1-7.5c-6.4-1.1-14.7-2.6-14.7-10.7c0-6.4,5.3-10.6,14.7-10.6c9.7,0,14.7,4.9,14.7,12.6h-7.1c0-5.3-1.8-9.2-8.2-9.2 c-4.4,0-6.7,2.7-6.7,6c0,4.1,4.4,5.7,10.6,6.7C221.6,18,229.1,20.1,229.1,28.1" />
            <path d="M250.4,1c-12.1,0-19.1,8-19.1,19.1c0,11.4,7.7,19.1,19.1,19.1s19-7.6,19-19.1C269.3,9,262.3,1,250.4,1 M261.7,22.4 c-1.8,6.6-6.5,15.1-15.5,12.8s-9.3-11.1-7.4-17.6c1.9-6.8,6.2-15.1,15.7-12.7C264.1,7.2,263.6,15.5,261.7,22.4" />
          </svg>
          <p className="text-base text-content-secondary mt-1">Content Approval Platform</p>
        </div>

        {/* Card */}
        <div className="bg-white border rounded-xl p-6 shadow-card">
          <h2 className="text-lg font-semibold text-content-primary mb-1">Create an account</h2>
          <p className="text-sm text-content-secondary mb-5">Join Social Soup to get started</p>

          <RegisterForm
            onSubmit={handleRegister}
            isLoading={registerMutation.isPending}
            error={registerMutation.error?.message ?? null}
          />

          <p className="text-sm text-content-secondary text-center mt-5">
            Already have an account?{' '}
            <Link to="/login" className="text-report-accent font-medium hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

// For lazy loading via React Router
export const Component = RegisterPage;
